import dayjs from 'dayjs';
import { inspectionService } from '@/services/inspection.service';
import { InspectionItem, InspectionItemStatus } from '@/services/types/inspection.types';
import { CalendarEvent, CalendarFilters } from './types';

// Mapeamento de nome do status (string) para enum numérico
const STATUS_NAME_TO_ENUM: Record<string, InspectionItemStatus> = {
  'AguardandoDirecionamento': InspectionItemStatus.AguardandoDirecionamento,
  'AguardandoAceitePrestadora': InspectionItemStatus.AguardandoAceitePrestadora,
  'PrestadoraRecusou': InspectionItemStatus.PrestadoraRecusou,
  'AguardandoDirecionamentoInspetor': InspectionItemStatus.AguardandoDirecionamentoInspetor,
  'AguardandoAceiteInspetor': InspectionItemStatus.AguardandoAceiteInspetor,
  'InspetorRecusou': InspectionItemStatus.InspetorRecusou,
  'AguardandoAgendamento': InspectionItemStatus.AguardandoAgendamento,
  'Agendado': InspectionItemStatus.Agendado,
  'CheckinRealizado': InspectionItemStatus.CheckinRealizado,
  'Finalizado': InspectionItemStatus.Finalizado,
  'Cancelado': InspectionItemStatus.Cancelado,
  'Frustrado': InspectionItemStatus.Frustrado,
  'AguardandoGeracaoLaudo': InspectionItemStatus.AguardandoGeracaoLaudo,
  'AguardandoRevisaoPrestadora': InspectionItemStatus.AguardandoRevisaoPrestadora,
  'LaudoNegadoPorPrestadora': InspectionItemStatus.LaudoNegadoPorPrestadora,
  'AguardandoRevisaoBanco': InspectionItemStatus.AguardandoRevisaoBanco,
  'LaudoNegadoPorBanco': InspectionItemStatus.LaudoNegadoPorBanco,
  'CorrecaoPeloInspetorBanco': InspectionItemStatus.CorrecaoPeloInspetorBanco,
  'LaudoAceitoPeloBanco': InspectionItemStatus.LaudoAceitoPeloBanco,
  'TelevistoriaAgendada': InspectionItemStatus.TelevistoriaAgendada,
  'TelevistoriaFinalizada': InspectionItemStatus.TelevistoriaFinalizada,
  'LaudoDevolvido': InspectionItemStatus.LaudoDevolvido,
  'AguardandoPreenchimentoPrestadora': InspectionItemStatus.AguardandoPreenchimentoPrestadora,
};

// Mapeamento de status para labels amigáveis (para exibir no calendário)
const STATUS_LABELS: Record<InspectionItemStatus, string> = {
  [InspectionItemStatus.AguardandoDirecionamento]: 'Aguardando Direcionamento',
  [InspectionItemStatus.AguardandoAceitePrestadora]: 'Aguardando Aceite Prestadora',
  [InspectionItemStatus.PrestadoraRecusou]: 'Prestadora Recusou',
  [InspectionItemStatus.AguardandoDirecionamentoInspetor]: 'Aguardando Direcionamento Inspetor',
  [InspectionItemStatus.AguardandoAceiteInspetor]: 'Aguardando Aceite Inspetor',
  [InspectionItemStatus.InspetorRecusou]: 'Inspetor Recusou',
  [InspectionItemStatus.AguardandoAgendamento]: 'Aguardando Agendamento',
  [InspectionItemStatus.Agendado]: 'Agendado',
  [InspectionItemStatus.CheckinRealizado]: 'Check-in Realizado',
  [InspectionItemStatus.Finalizado]: 'Finalizado',
  [InspectionItemStatus.Cancelado]: 'Cancelado',
  [InspectionItemStatus.Frustrado]: 'Frustrado',
  [InspectionItemStatus.AguardandoGeracaoLaudo]: 'Aguardando Geração Laudo',
  [InspectionItemStatus.AguardandoRevisaoPrestadora]: 'Aguardando Revisão Prestadora',
  [InspectionItemStatus.LaudoNegadoPorPrestadora]: 'Laudo Negado Por Prestadora',
  [InspectionItemStatus.AguardandoRevisaoBanco]: 'Aguardando Revisão Banco',
  [InspectionItemStatus.LaudoNegadoPorBanco]: 'Laudo Negado Por Banco',
  [InspectionItemStatus.CorrecaoPeloInspetorBanco]: 'Correção Pelo Inspetor',
  [InspectionItemStatus.LaudoAceitoPeloBanco]: 'Laudo Aceito Pelo Banco',
  [InspectionItemStatus.TelevistoriaAgendada]: 'Televistoria Agendada',
  [InspectionItemStatus.TelevistoriaFinalizada]: 'Televistoria Finalizada',
  [InspectionItemStatus.LaudoDevolvido]: 'Laudo Devolvido',
  [InspectionItemStatus.AguardandoPreenchimentoPrestadora]: 'Aguardando Preenchimento Prestadora',
};

export class CalendarService {
  /**
   * Busca eventos do calendário baseado nos filtros e intervalo de datas
   */
  async getCalendarEvents(
    startDate: Date,
    endDate: Date,
    filters?: CalendarFilters
  ): Promise<CalendarEvent[]> {
    try {
      const params: any = {
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
        pageSize: 1000, // Get all events in the date range
      };

      // Adicionar filtros
      if (filters?.statuses && filters.statuses.length > 0) {
        params.statuses = filters.statuses.map(s => s.toString());
      }

      // IMPORTANTE: Se temos inspectorId, passar APENAS inspectorId
      // porque o filtro AND (providerId + inspectorId) retorna 0 resultados
      if (filters?.inspectorId) {
        params.inspectorId = filters.inspectorId;
      } else if (filters?.providerId) {
        params.providerId = filters.providerId;
      }

      const { items } = await inspectionService.getInspections(params);

      // Transformar inspection items em calendar events
      const events = this.transformToCalendarEvents(items);

      return events;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Transforma InspectionItem[] em CalendarEvent[]
   */
  private transformToCalendarEvents(items: InspectionItem[]): CalendarEvent[] {
    // Filtrar itens que possuem scheduledDate OU videoConferenceScheduledStartTime
    const itemsWithSchedules = items.filter(item => {
      const itemAny = item as any;
      return itemAny.scheduledDate || itemAny.videoConferenceScheduledStartTime;
    });

    return itemsWithSchedules
      .flatMap(item => {
        const itemAny = item as any;

        // Converter status de string para número usando mapeamento
        let statusNum: number;
        if (typeof item.status === 'string') {
          statusNum = STATUS_NAME_TO_ENUM[item.status] ?? InspectionItemStatus.AguardandoDirecionamento;
        } else {
          statusNum = item.status;
        }

        // Verificar se tem agendamento de video conferência
        const hasVideoConference = itemAny.videoConferenceScheduledStartTime;

        // Para televistorias, usar a data da video conferência
        // Para inspeções regulares, usar a scheduledDate
        let scheduleDate: dayjs.Dayjs;
        let start: Date;
        let end: Date;
        let title: string;

        if (hasVideoConference) {
          // TELEVISTORIA: usar data/hora específica da video conferência
          scheduleDate = dayjs(itemAny.videoConferenceScheduledStartTime);
          start = scheduleDate.toDate();

          // Calcular fim baseado no videoConferenceScheduledEndTime ou adicionar 2 horas
          if (itemAny.videoConferenceScheduledEndTime) {
            end = dayjs(itemAny.videoConferenceScheduledEndTime).toDate();
          } else {
            end = scheduleDate.add(2, 'hours').toDate();
          }

          // Para TelevistoriaAgendada, mostrar horário. Para outros, mostrar o status
          if (statusNum === InspectionItemStatus.TelevistoriaAgendada) {
            const hourStr = scheduleDate.format('HH:mm');
            title = `Televistoria - ${hourStr}`;
          } else {
            title = STATUS_LABELS[statusNum] || item.statusDescription || 'Televistoria';
          }
        } else if (itemAny.scheduledDate) {
          // VISTORIA REGULAR: usar scheduledDate com período do dia
          scheduleDate = dayjs(itemAny.scheduledDate);

          // Mapear timeSlot para período do dia e horários
          let startHour = 9;
          let endHour = 12;
          let periodo = 'Manhã';

          // Se a API não tiver timeSlot, usar período padrão
          // timeSlot pode estar em schedules ou diretamente no item
          const timeSlot = itemAny.timeSlot;

          if (timeSlot === 1) {
            startHour = 8;
            endHour = 12;
            periodo = 'Manhã';
          } else if (timeSlot === 2) {
            startHour = 12;
            endHour = 18;
            periodo = 'Tarde';
          } else if (timeSlot === 3) {
            startHour = 18;
            endHour = 22;
            periodo = 'Noite';
          }

          start = scheduleDate.hour(startHour).minute(0).toDate();
          end = scheduleDate.hour(endHour).minute(0).toDate();

          // Para Agendado, mostrar turno. Para outros, mostrar o status
          if (statusNum === InspectionItemStatus.Agendado) {
            title = `Vistoria - ${periodo}`;
          } else {
            title = STATUS_LABELS[statusNum] || item.statusDescription || 'Vistoria';
          }
        } else {
          // Não tem nem scheduledDate nem videoConference
          return [];
        }

        // Buscar informações do inspetor se houver assignment
        let inspectorName: string | undefined;
        let inspectorId: string | undefined;
        let providerName: string | undefined;
        let providerId: string | undefined;

        if (itemAny.assignments && itemAny.assignments.length > 0) {
          const latestAssignment = itemAny.assignments[itemAny.assignments.length - 1];
          providerName = latestAssignment.providerName;
          providerId = latestAssignment.providerId;

          // Se tiver inspetor atribuído
          if (latestAssignment.inspectorName) {
            inspectorName = latestAssignment.inspectorName;
            inspectorId = latestAssignment.inspectorId;
          }
        }

        // Endereço formatado
        const address = itemAny.address || `${item.street || ''}${item.number ? ', ' + item.number : ''}`;

        return [{
          id: item.id!,
          title,
          start,
          end,
          status: statusNum!,
          statusDescription: item.statusDescription!,
          inspectionItemId: item.id!,
          proposalNumber: itemAny.proposalNumber || item.id!,
          address,
          clientName: itemAny.clientName,
          inspectorName,
          inspectorId,
          providerName,
          providerId,
          scheduleId: undefined,
          timeSlot: undefined,
          timeSlotDescription: undefined,
          notes: undefined,
        }];
      });
  }

  /**
   * Busca detalhes de um evento específico
   */
  async getEventDetails(inspectionItemId: string): Promise<InspectionItem> {
    return await inspectionService.getInspectionItemById(inspectionItemId);
  }
}

export const calendarService = new CalendarService();
